/**
 * Copy text to the clipboard, on plain HTTP as well as HTTPS.
 *
 * `navigator.clipboard` only exists in a SECURE CONTEXT — HTTPS, or localhost.
 * Trinetra's nginx serves the UI over plain HTTP on port 80, so every operator
 * reaching a node by LAN IP (`http://192.168.x.x`) has no `navigator.clipboard`
 * at all: the property is undefined, not merely permission-denied. Guarding on
 * it and giving up is what produced "Could not copy to clipboard" on the license
 * page for exactly the string that is hardest to retype by hand — a
 * 64-character fingerprint.
 *
 * So fall back to the deprecated `document.execCommand('copy')`, which has no
 * secure-context requirement. It is deprecated but universally implemented, and
 * it is the only thing that works here until the UI is served over TLS.
 *
 * Must be called from a user-gesture handler (a click) — both paths require it.
 */

/** Pre-secure-context fallback: select a detached textarea and copy from it. */
function copyViaExecCommand(text: string): boolean {
    const area = document.createElement('textarea');
    area.value = text;

    // Off-screen rather than display:none / hidden — an unrendered element
    // cannot be selected, so the copy would silently do nothing.
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    area.style.pointerEvents = 'none';

    document.body.appendChild(area);

    // Remember where focus was: stealing it and not giving it back leaves the
    // page feeling broken after a copy (lost keyboard position, closed menus).
    const previous = document.activeElement as HTMLElement | null;

    try {
        area.focus();
        area.select();
        // iOS ignores select() on a readonly field; setSelectionRange is what
        // actually marks the range there.
        area.setSelectionRange(0, text.length);
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        area.remove();
        previous?.focus?.();
    }
}

/**
 * Copy `text`, returning whether it landed on the clipboard.
 * Never throws — callers decide what to tell the operator.
 */
export async function copyText(text: string): Promise<boolean> {
    if (!text) {
        return false;
    }

    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Present but refused — the document was not focused, or a
            // permissions policy blocked it. The fallback often still works,
            // so this is not the end of the attempt.
        }
    }

    return copyViaExecCommand(text);
}

export default copyText;
